//@flow
import EntityImportDataHelper from 'common/entity/helpers/EntityImportDataHelper';
import { FleetTypeEnum } from 'common/models/internal/MasterVehicleModel';
import DataUtils from 'common/utils/DataUtils';
import * as Immutable from 'immutable';
import { TransporterId } from 'common/models/internal/TransporterId';
import { RiderId } from 'common/models/internal/RiderId';
import I18nHelper from 'common/helpers/I18nHelper';
import NumberUtils from 'common/utils/NumberUtils';
import moment from 'moment-timezone';
import { EntityImportHelper } from 'common/entity/helpers/EntityImportHelper';
import { MasterVehicle } from 'common/models/internal/MasterVehicle';

const translationsNamespace = ['validations'];
const nsTranslate = I18nHelper.getTranslate(translationsNamespace);

export class VehicleImportHelper extends EntityImportDataHelper {
    static processVehicleModelId({ cellValue }) {
        return cellValue.trim();
    }

    static processFleetType({ cellValue }) {
        return cellValue.trim();
    }

    static validateFleetType({ entity }) {
        const { fleetType } = entity;
        const fleetTypeEnumValues = FleetTypeEnum.values();
        if (DataUtils.isEmpty(fleetType)) {
            return nsTranslate('vehicleValidationErrors.fleetTypeEmpty');
        }
        if (!fleetTypeEnumValues.includes(fleetType)) {
            return nsTranslate('vehicleValidationErrors.invalidFleetType', {
                typeList: fleetTypeEnumValues.join(', ')
            });
        }
    }

    /**
     * This method takes `transporterId` and @returns `TransporterId` object of `Immutable.Map`
     * Used for edit masters table
     * @static
     * @memberof EntityImportDataHelper
     */
    static serializeTransporterId = (transporterId: string): Immutable.Map =>
        TransporterId.fromJS({ transporterId });
    /**
     * This method takes object arg as accessor and returns top level accessor for transporterId
     *
     * @static
     * @memberof VehicleImportHelper
     */
    static getTransporterIdAccessor = ({
        accessor
    }: Object): Immutable.List<string> => accessor.pop();

    static serializeRiderId = (riderId: string): Immutable.Map =>
        RiderId.fromJS({ riderId });

    static getRiderIdAccessor = ({ accessor }): Immutable.List =>
        accessor.pop();

    static processManufacturingDate({ cellValue }: Object): string {
        return cellValue.trim();
    }

    static validateManufacturingDate({ entity }: Object): ?string {
        const {
            manufacturingDate: { year }
        } = entity;
        if (
            !DataUtils.isEmpty(year) &&
            (!NumberUtils.isNumeric(year) ||
                !NumberUtils.isInteger(year) ||
                year > moment().year())
        ) {
            return nsTranslate(
                'vehicleValidationErrors.invalidManufacturingDate'
            );
        }
    }

    static getCustomFields(entitySettings) {
        return entitySettings?.vehicleSettings?.customFields;
    }

    static processEntityRows = ({ rows }, { clientId }) => {
        return rows.map(entityRow =>
            entityRow.map((cellValue, headerId) => {
                switch (headerId) {
                    case 'customProperties':
                        return cellValue.filter(value => value);
                    case 'riderId':
                        return cellValue &&
                            !DataUtils.isEmpty(cellValue.get('riderId'))
                            ? cellValue.set('clientId', clientId)
                            : null;
                    case 'vehicleModelId':
                        return cellValue &&
                            !DataUtils.isEmpty(cellValue.get('modelId'))
                            ? cellValue.set('clientId', clientId)
                            : null;
                    default:
                        return cellValue;
                }
            })
        );
    };

    static getUploadedEntities = ({ uploadedTable, getDataForCell }) => {
        const vehiclesData = EntityImportHelper.makeDataForEntities({
            table: uploadedTable,
            getDataForCell,
            processEntityRows: VehicleImportHelper.processEntityRows
        });
        const vehicleEntities = Immutable.List(
            vehiclesData.map(vehicleEntity =>
                MasterVehicle.fromJS(vehicleEntity.toJS())
            )
        );
        return vehicleEntities;
    };

    static generateEntityList = (
        vehiclesData: Array<*>
    ): Immutable.List<MasterVehicle> =>
        Immutable.List(
            vehiclesData.map(vehicleData => MasterVehicle.fromJS(vehicleData))
        );
}

export default VehicleImportHelper;
